import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Layout } from "./components/Layout";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";

import Index from "./pages/Index";
import About from "./pages/About";
import Features from "./pages/Features";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import NotFound from "./pages/NotFound";

import Dashboard from "./pages/Dashboard";
import RegisterDeal from "./pages/RegisterDeal";
import DocumentUpload from "./pages/DocumentUpload";
import AIReview from "./pages/AIReview";
import ReportGeneration from "./pages/ReportGeneration";
import Deals from "./pages/Deals";
import ViewDeal from "./pages/ViewDeal";
import Documents from "./pages/Documents";
import AIInsights from "./pages/AIInsights";
import Settings from "./pages/Settings";
import ApiTest from "./pages/ApiTest";
import NotificationPage from "./pages/NotificationPage";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPasswordOtp from "./pages/ResetPasswordOtp";

import Forbidden from "./pages/Forbidden";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<PublicRoute><Index /></PublicRoute>} />
              <Route path="/about" element={<PublicRoute><About /></PublicRoute>} />
              <Route path="/features" element={<PublicRoute><Features /></PublicRoute>} />
              <Route path="/contact" element={<PublicRoute><Contact /></PublicRoute>} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
              <Route path="/verify-email" element={<PublicRoute><VerifyEmail /></PublicRoute>} />
              <Route path="/reset-password/otp" element={<PublicRoute><ResetPasswordOtp /></PublicRoute>} />

              {/* Protected routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Layout><Dashboard /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/deals" element={
                <ProtectedRoute>
                  <Layout><Deals /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/deals/:dealId" element={
                <ProtectedRoute>
                  <Layout><ViewDeal /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/documents" element={
                <ProtectedRoute>
                  <Layout><Documents /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/ai-insights" element={
                <ProtectedRoute>
                  <Layout><AIInsights /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/settings" element={
                <ProtectedRoute>
                  <Layout><Settings /></Layout>
                </ProtectedRoute>
              } />

              {/* /register-deal → ADMIN only */}
              <Route path="/register-deal" element={
                <ProtectedRoute requiredRoles={['ADMIN']}>
                  <Layout><RegisterDeal /></Layout>
                </ProtectedRoute>
              } />

              {/* /document-upload → ADMIN or ANALYST */}
              <Route path="/document-upload" element={
                <ProtectedRoute requiredRoles={['ADMIN','ANALYST']}>
                  <Layout><DocumentUpload /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/ai-review" element={
                <ProtectedRoute>
                  <Layout><AIReview /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/report-generation" element={
                <ProtectedRoute>
                  <Layout><ReportGeneration /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/change-password" element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              } />

              <Route path="/api-test" element={
                <ProtectedRoute>
                  <Layout><ApiTest /></Layout>
                </ProtectedRoute>
              } />

              <Route path="/notifications" element={
                <ProtectedRoute>
                  <Layout><NotificationPage /></Layout>
                </ProtectedRoute>
              } />

              {/* 403 route */}
              <Route path="/forbidden" element={<Forbidden />} />

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
